import React from "react";
import { Box, Chip, useTheme } from "@mui/material";
import CustomTypography from "../../../../../../views/components/common/form/CustomTypography";
import AppButton from "../../../../../../views/components/common/buttons/AppButton";
import { InventoryOpsQcStatus } from "../../../../../../models/ProjectManagement/ProjectInventoryProduct";
import { InventoryOpsQcSnapshot } from "./inventoryOpsQc";

interface QcStatusSummaryProps {
  snapshot: InventoryOpsQcSnapshot;
  /** Sends `pass_remaining: true` for UNCONFIRMED rows. */
  onConfirm: () => void;
  confirmDisabled?: boolean;
}

const STATUS_LABELS: Record<InventoryOpsQcStatus, string> = {
  NOT_STARTED: "Not started",
  IN_PROGRESS: "In progress",
  COMPLETED: "Completed",
  UNCONFIRMED: "Not confirmed",
};

/** QC status chip, Received · Passed · Rejected · Pending, and confirm banner. */
const QcStatusSummary: React.FC<QcStatusSummaryProps> = ({
  snapshot,
  onConfirm,
  confirmDisabled,
}) => {
  const theme = useTheme();
  const { status } = snapshot;

  if (!status) return null;
  if (snapshot.received <= 0) {
    return snapshot.countAtQc ? (
      <CustomTypography
        variant="heading8"
        color={theme.palette.customColors.greyTextColor}
        style={{ marginBottom: 12, display: "block" }}
      >
        Marked received. Count the stock here: tested quantity is added to the
        received total.
      </CustomTypography>
    ) : null;
  }

  const chipColor = {
    NOT_STARTED: theme.palette.customColors.greyTextColor,
    IN_PROGRESS: theme.palette.warning.main,
    COMPLETED: theme.palette.success.main,
    UNCONFIRMED: theme.palette.warning.main,
  }[status];

  const stats: Array<{ label: string; value: number | null }> = [
    { label: "Received", value: snapshot.received },
    { label: "Passed", value: snapshot.passed },
    { label: "Rejected", value: snapshot.rejected },
    // Count at QC: everything counted was tested, so pending is always 0.
    ...(snapshot.countAtQc
      ? []
      : [{ label: "Pending", value: snapshot.pending }]),
  ];

  return (
    <Box sx={{ mb: 1.5 }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          flexWrap: "wrap",
          gap: 1.5,
        }}
      >
        <Chip
          size="small"
          label={STATUS_LABELS[status]}
          sx={{
            fontSize: "12px",
            fontWeight: 600,
            color: chipColor,
            backgroundColor: `${chipColor}1A`,
          }}
        />
        {stats.map((stat) => (
          <CustomTypography
            key={stat.label}
            variant="heading8"
            color={theme.palette.customColors.greyTextColor}
          >
            {stat.label}:{" "}
            <span style={{ fontWeight: 600, color: "black" }}>
              {stat.value ?? "—"}
            </span>
          </CustomTypography>
        ))}
      </Box>

      {(status === "UNCONFIRMED" || snapshot.confirmOnly) && (
        <Box
          sx={{
            mt: 1,
            px: 1.5,
            py: 1,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 2,
            flexWrap: "wrap",
            borderRadius: "6px",
            backgroundColor: `${theme.palette.warning.main}14`,
            border: `1px solid ${theme.palette.warning.main}33`,
          }}
        >
          <Box>
            <CustomTypography variant="heading7" style={{ fontWeight: 600 }}>
              QC not confirmed
            </CustomTypography>
            <CustomTypography
              variant="heading8"
              color={theme.palette.customColors.greyTextColor}
            >
              {snapshot.confirmOnly
                ? "Stock from this inward is already at the printer. Enter any rejections, then confirm to pass everything else."
                : "Passed quantity was never recorded. Confirm to pass everything not rejected."}
            </CustomTypography>
          </Box>
          <AppButton
            label="Confirm QC"
            variant="secondary"
            onClick={onConfirm}
            disabled={confirmDisabled}
          />
        </Box>
      )}
    </Box>
  );
};

export default QcStatusSummary;
